import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Persistence for signup v2 (docs/signup-v2/02-ux.md §6).
 * - Signup draft (no password, ever): sessionStorage.
 * - Onboarding state per store: localStorage `ecometri.onboardingV2.{storeId}`.
 * - Product draft (name + price): sessionStorage.
 * Demo mode uses separate sessionStorage keys so it never mixes with real data.
 */

export const DRAFT_KEY = 'ecometri.signupV2.draft';
export const PRODUCT_DRAFT_KEY = 'ecometri.onboardingV2.productDraft';
export const DEMO_DRAFT_KEY = 'ecometri.signupV2.demo.draft';
export const DEMO_ONBOARDING_KEY = 'ecometri.signupV2.demo.onboarding';
export const DEMO_PRODUCT_DRAFT_KEY = 'ecometri.signupV2.demo.productDraft';

export const getOnboardingKey = (storeId) => `ecometri.onboardingV2.${storeId}`;

/**
 * App calls `logOut()` (localStorage.clear + sessionStorage.clear) on every load without a
 * session. It can run before the step mounts or, when /status is slower than App's 600ms loader,
 * after it. Two safety nets: a snapshot taken when this module is evaluated (before the wipe), and
 * a flush of the in-memory value on `pagehide` (see usePersistedObject).
 */
const BOOT_SNAPSHOT = {};
try {
    [
        DRAFT_KEY,
        PRODUCT_DRAFT_KEY,
        DEMO_DRAFT_KEY,
        DEMO_ONBOARDING_KEY,
        DEMO_PRODUCT_DRAFT_KEY,
    ].forEach((key) => {
        const value = window.sessionStorage.getItem(key);
        if (value) BOOT_SNAPSHOT[key] = value;
    });
} catch {
    /* storage blocked */
}

const getStorage = (type) => {
    try {
        return type === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
        return null;
    }
};

export const readJson = (type, key) => {
    try {
        const storage = getStorage(type);
        let raw = storage ? storage.getItem(key) : null;
        if (!raw && type === 'session' && BOOT_SNAPSHOT[key]) {
            raw = BOOT_SNAPSHOT[key];
            if (storage) storage.setItem(key, raw);
        }
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

export const writeJson = (type, key, value) => {
    try {
        const storage = getStorage(type);
        if (storage) storage.setItem(key, JSON.stringify(value));
    } catch {
        /* storage full or blocked: keep working in memory */
    }
};

export const removeKey = (type, key) => {
    delete BOOT_SNAPSHOT[key];
    try {
        const storage = getStorage(type);
        if (storage) storage.removeItem(key);
    } catch {
        /* ignore */
    }
};

/**
 * Small persisted object state. `update` merges a patch and writes it synchronously, so a
 * navigation right after an update already sees the new value.
 */
const usePersistedObject = (type, key) => {
    const [value, setValue] = useState(() => (key ? readJson(type, key) : {}));
    const valueRef = useRef(value);
    const keyRef = useRef(key);

    if (keyRef.current !== key) {
        keyRef.current = key;
        valueRef.current = key ? readJson(type, key) : {};
    }

    const update = useCallback(
        (patch) => {
            const next = { ...valueRef.current, ...patch };
            valueRef.current = next;
            if (key) writeJson(type, key, next);
            setValue(next);
            return next;
        },
        [type, key]
    );

    // Re-persist on reload/close in case App's logOut() wiped the storage after mount.
    useEffect(() => {
        if (!key) return undefined;
        const flush = () => {
            if (Object.keys(valueRef.current).length > 0) writeJson(type, key, valueRef.current);
        };
        window.addEventListener('pagehide', flush);
        return () => window.removeEventListener('pagehide', flush);
    }, [type, key]);

    const clear = useCallback(() => {
        valueRef.current = {};
        if (key) removeKey(type, key);
        setValue({});
    }, [type, key]);

    return [valueRef.current, update, clear];
};

/** Signup draft (steps 1–2). Never stores the password. */
export const useSignupDraft = (isDemo) =>
    usePersistedObject('session', isDemo ? DEMO_DRAFT_KEY : DRAFT_KEY);

/** Onboarding state (steps 3–5) for a store. */
export const useOnboardingState = (storeId, isDemo) => {
    let key = null;
    if (isDemo) key = DEMO_ONBOARDING_KEY;
    else if (storeId) key = getOnboardingKey(storeId);
    return usePersistedObject(isDemo ? 'session' : 'local', key);
};

/** Product draft (step 4): name and price only, the photo cannot be serialized. */
export const useProductDraft = (isDemo) =>
    usePersistedObject('session', isDemo ? DEMO_PRODUCT_DRAFT_KEY : PRODUCT_DRAFT_KEY);

/** Writes the onboarding state of a store without a hook (used right after login). */
export const writeOnboardingState = (storeId, patch) => {
    const key = getOnboardingKey(storeId);
    writeJson('local', key, { ...readJson('local', key), ...patch });
};

/** Clears every demo key (used by "Ir a mi panel" in demo mode). */
export const clearDemoState = () => {
    [DEMO_DRAFT_KEY, DEMO_ONBOARDING_KEY, DEMO_PRODUCT_DRAFT_KEY].forEach((key) =>
        removeKey('session', key)
    );
};
